import React, { FC, useCallback, useContext, useEffect } from 'react';
import AppleSignin from 'react-apple-signin-auth';
import { useMutation } from 'react-apollo';
import { useQueryParam, StringParam } from 'use-query-params';

import AppleButton from './AppleButton';
import { SignInContext } from 'components/context/SignInContext';

import { onError } from 'graphql/helpers';

import { COMPLETE_SIGN_UP, SIGN_IN_APPLE } from '../../../graphql/mutations/auth';

import { ButtonProps } from 'vibo-ui/Button';
import { GA4LoginSource } from 'components/context/GA4Context/interfaces';
import { AppleSigninRenderProps } from './interfaces';
import { UserAction } from 'types/enums';

const AppleLoginButton: FC<ButtonProps> = props => {
  const clientId = process.env.REACT_APP_APPLE_CLIENT_ID!;

  const [token] = useQueryParam('id_token', StringParam);
  const [success] = useQueryParam('success', StringParam);
  const [url] = useQueryParam('url', StringParam);

  const { handleLogUserIn } = useContext(SignInContext);

  const [completeSignup] = useMutation<CompleteSignupResponse>(COMPLETE_SIGN_UP, {
    onCompleted: data => handleAppleLogIn(data.completeSignup),
    onError,
  });

  const [signInApple, { loading }] = useMutation<SignInAppleResponse & AuthResponse>(
    SIGN_IN_APPLE,
    {
      variables: {
        token,
        clientId,
      },
      onCompleted: data => {
        if (data.signInApple.action === UserAction.COMPLETE_SIGN_UP) {
          completeSignup({
            variables: {
              payload: data.signInApple.data,
              token: data.signInApple.token,
            },
          });
        } else {
          handleAppleLogIn(data.signInApple);
        }
      },
      onError,
    }
  );

  const handleAppleLogIn = useCallback((variables: SocialSignInResponse) => {
    handleLogUserIn(
      {
        variables,
      },
      GA4LoginSource.apple
    );
  }, []);

  const responseApple = useCallback(() => {
    signInApple({
      variables: {
        token,
        clientId,
      },
    });
  }, [token]);

  useEffect(() => {
    if (token && Boolean(success)) {
      localStorage.removeItem('appleLoginSearch');
      responseApple();
    }
  }, [token, success]);

  return (
    <AppleSignin
      authOptions={{
        clientId,
        scope: 'email name',
        redirectURI: process.env.REACT_APP_APPLE_REDIRECT_URL,
        state: process.env.REACT_APP_APPLE_STATE || 'web',
        usePopup: false,
      }}
      responsetype={'id_token code'}
      responsemode={'form_post'}
      render={({ onClick }: AppleSigninRenderProps) => (
        <AppleButton
          {...props}
          onClick={e => {
            if (!!url) {
              localStorage.setItem('appleLoginSearch', url);
            }

            onClick(e);
          }}
          loading={loading}
        />
      )}
    />
  );
};

export default AppleLoginButton;
